import { useEffect, useState } from "react";
import { Loader2, Check, X, Sparkles, RotateCcw, Instagram, Youtube, Users } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import AmbassadorBadge from "@/components/AmbassadorBadge";

/**
 * AmbassadorsTab — admin review + management panel.
 *
 * Two lists in one tab:
 *  • Pending applications — approve inline. Shows pitch + current
 *    referral count so admins can tell if this person is already growing
 *    the community.
 *  • Active + revoked ambassadors — revoke or re-approve inline.
 */
export default function AmbassadorsTab() {
  const [pending, setPending] = useState([]);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        api.get("/admin/ambassador/applications"),
        api.get("/admin/ambassador/list"),
      ]);
      setPending(p.data || []);
      setRoster(r.data || []);
    } catch {
      toast.error("Could not load ambassadors.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/ambassador/${id}/approve`);
      toast.success(`Approved · ${data.tier} tier`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed.");
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this ambassador's status? Any unpaid commission balance is forfeited.")) return;
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/ambassador/${id}/revoke`);
      const cents = Number(data?.forfeited_cents || 0);
      toast.success(cents > 0 ? `Revoked. $${(cents / 100).toFixed(2)} in unpaid commissions forfeited.` : "Revoked.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed.");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const reason = window.prompt(
      "Optional feedback for the applicant (leave blank to reject silently):",
      "",
    );
    if (reason === null) return; // user hit Cancel
    setBusyId(id);
    try {
      await api.post(`/admin/ambassador/${id}/reject`, { reason: reason.trim() || null });
      toast.success("Application rejected.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-zinc-500">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading ambassadors…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-ambassadors-tab">
      {/* Pending */}
      <section>
        <div className="text-xs uppercase tracking-[0.3em] text-amber-400 mb-3 flex items-center gap-2">
          <Sparkles size={14} /> Pending applications
          {pending.length > 0 && (
            <span className="text-[10px] bg-amber-400 text-black rounded-full px-2">
              {pending.length}
            </span>
          )}
        </div>
        {pending.length === 0 ? (
          <div className="text-sm text-zinc-500">No pending applications.</div>
        ) : (
          <div className="space-y-3">
            {pending.map((u) => (
              <ApplicationCard
                key={u.id}
                user={u}
                busy={busyId === u.id}
                onApprove={() => approve(u.id)}
                onReject={() => reject(u.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Roster */}
      <section>
        <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-3">
          Active + Revoked ({roster.length})
        </div>
        {roster.length === 0 ? (
          <div className="text-sm text-zinc-500">No ambassadors yet.</div>
        ) : (
          <div className="grid gap-2">
            {roster.map((u) => (
              <div
                key={u.id}
                data-testid={`ambassador-row-${u.handle}`}
                className="flex items-center gap-3 bg-black/40 border border-white/10 rounded-md px-3 py-2"
              >
                <Avatar className="w-9 h-9 border border-white/10">
                  <AvatarImage src={u.avatar_url} />
                  <AvatarFallback className="bg-rose-600 text-white text-xs">
                    {u.handle?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <div className="text-sm text-white truncate">{u.display_name}</div>
                    <AmbassadorBadge status={u.ambassador_status} tier={u.ambassador_tier} />
                  </div>
                  <div className="text-[11px] text-zinc-500">
                    {u.display_name} · {u.referrals_count || 0} refs
                    {u.ambassador_status === "revoked" && " · revoked"}
                  </div>
                </div>
                {u.ambassador_status === "approved" ? (
                  <button
                    type="button"
                    onClick={() => revoke(u.id)}
                    disabled={busyId === u.id}
                    data-testid={`ambassador-revoke-${u.id}`}
                    className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1"
                  >
                    <X size={12} /> Revoke
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => approve(u.id)}
                    disabled={busyId === u.id}
                    data-testid={`ambassador-reapprove-${u.id}`}
                    className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1"
                  >
                    <RotateCcw size={12} /> Re-approve
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ApplicationCard({ user, busy, onApprove, onReject }) {
  const socials = user.ambassador_socials || {};
  const socialEntries = [
    { platform: "instagram", label: "Instagram", icon: Instagram, url: (h) => `https://instagram.com/${h}` },
    { platform: "tiktok",    label: "TikTok",    icon: Sparkles,  url: (h) => `https://tiktok.com/@${h}` },
    { platform: "x",         label: "X",         icon: Sparkles,  url: (h) => `https://x.com/${h}` },
    { platform: "youtube",   label: "YouTube",   icon: Youtube,   url: (h) => `https://youtube.com/@${h}` },
  ].filter((s) => socials[s.platform]);
  const followerCount = Number(user.ambassador_follower_count || 0);
  return (
    <div
      className="border border-white/10 rounded-md p-3 bg-black/40"
      data-testid={`ambassador-application-${user.handle}`}
    >
      <div className="flex items-center gap-3">
        <Avatar className="w-10 h-10 border border-white/10">
          <AvatarImage src={user.avatar_url} />
          <AvatarFallback className="bg-rose-600 text-white">
            {user.handle?.[0]?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-white truncate">{user.display_name}</div>
          <div className="text-[11px] text-zinc-500">
            {user.display_name} · {user.referrals_count || 0} refs · applied {new Date(user.ambassador_applied_at).toLocaleDateString()}
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={onReject}
            disabled={busy}
            data-testid={`ambassador-reject-${user.id}`}
            className="bg-transparent border border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-60 text-rose-300 text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1"
          >
            <X size={12} /> Reject
          </button>
          <button
            type="button"
            onClick={onApprove}
            disabled={busy}
            data-testid={`ambassador-approve-${user.id}`}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black text-xs font-semibold rounded-full px-3 py-1.5 flex items-center gap-1"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Approve
          </button>
        </div>
      </div>
      {(socialEntries.length > 0 || followerCount > 0) && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px]">
          {followerCount > 0 && (
            <span
              className="inline-flex items-center gap-1 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-full px-2 py-1"
              data-testid={`ambassador-application-${user.handle}-followers`}
            >
              <Users size={10} /> {followerCount.toLocaleString()} followers
            </span>
          )}
          {socialEntries.map((s) => (
            <a
              key={s.platform}
              href={s.url(socials[s.platform])}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`ambassador-application-${user.handle}-social-${s.platform}`}
              className="inline-flex items-center gap-1 bg-white/5 border border-white/10 hover:border-rose-500/40 hover:text-rose-300 text-zinc-300 rounded-full px-2 py-1 transition"
            >
              <s.icon size={10} /> {s.label} · @{socials[s.platform]}
            </a>
          ))}
        </div>
      )}
      {user.ambassador_pitch && (
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap border-l-2 border-amber-500/40 pl-3">
          {user.ambassador_pitch}
        </p>
      )}
    </div>
  );
}
